import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';

/*
 * PageMorph — a quiet page-OPEN fade for the MAIN sidebar pages only.
 *
 * Why a decoupled overlay (not a clip-path on each routed node): the app's main
 * pages each mount their own layout (PageLayout / QAManagerLayout /
 * KYCGoalsLayout …), so there is no single wrappable <Outlet>. A theme-coloured
 * panel that fades out reads as the new page fading in, works uniformly across
 * every layout, and — being `pointer-events-none` — never blocks input
 * ("ne smeta pri radu"). Opacity only: no wipe, no movement — deliberately
 * understated (the clip-path/ink-line version read as too much).
 *
 * Scope: fires ONLY when the FIRST path segment (the "section") changes AND the
 * new section is one of the sidebar's main pages — so sub-tab navigation
 * (e.g. /qa-manager/tickets → /qa-manager/coaching) does NOT trigger it.
 *
 * Honours prefers-reduced-motion (renders nothing).
 *
 * Note: a stale, unused components/PageTransition.jsx (wheel-nav wrapper,
 * imported nowhere) predates this; this is the active page-transition.
 */

const MAIN_SECTIONS = new Set([
  'home', 'tools', 'knowledge-base', 'goals', 'vip-host-metrics',
  'developer-dashboard', 'monitoring', 'user-management', 'qa-manager', 'kyc-goals',
  'payments-goals', 'crypto-goals', 'kyc-stats', 'tl', 'everything',
]);

const EASE = [0.4, 0, 0.2, 1]; // Material standard
const DURATION = 0.26;

const sectionOf = (pathname) => (pathname || '').split('/').filter(Boolean)[0] || 'home';

export default function PageMorph() {
  const location = useLocation();
  const reduce = useReducedMotion();
  const section = sectionOf(location.pathname);
  const prevSection = useRef(section);
  const [play, setPlay] = useState(null); // unique key per play, or null

  useEffect(() => {
    if (prevSection.current !== section) {
      const from = prevSection.current;
      prevSection.current = section;
      if (MAIN_SECTIONS.has(section)) {
        setPlay(`${from}->${section}-${Date.now()}`);
      }
    }
  }, [section]);

  if (reduce || !play) return null;

  return createPortal(
    <AnimatePresence>
      {/* Theme-coloured panel covers the content area, then fades out → the new
          page reads as fading in. The primary rail stays visible on desktop:
          its live width (64px collapsed / 224px expanded) is the --app-rail-w
          variable AppSidebar publishes, so the curtain never covers it. */}
      <motion.div
        key={play}
        className="fixed inset-y-0 right-0 left-0 lg:left-[var(--app-rail-w,64px)] z-[45] pointer-events-none bg-white dark:bg-[#0A0A0B]"
        initial={{ opacity: 1 }}
        animate={{ opacity: 0 }}
        transition={{ duration: DURATION, ease: EASE }}
        style={{ willChange: 'opacity' }}
        onAnimationComplete={() => setPlay(null)}
      />
    </AnimatePresence>,
    document.body
  );
}
